import { type RefObject, useId, useRef } from 'react';
import { useOpenerRef } from './useOpenerRef';
import { useDialogFocus } from './useDialogFocus';
import { useGateOpen } from './useGateOpen';

interface PromptDialogOptions {
  /**
   * Step aside while an interceptor gate (the agreement, the norms, the
   * 402 / 403 prompt or the plan selector it opens) is up: the gate is the
   * top layer, so focus, Tab and Escape are its until it is gone, and then
   * this dialog takes them back. On by default; off for the gate itself.
   */
  yieldToGate?: boolean;
  /**
   * Hand focus back to the opener when the dialog closes. On by default; off
   * when the close hands over to another layer that takes focus (the 402
   * prompt's "See plans": the plan selector, not the page under it).
   */
  returnFocus?: boolean;
  /**
   * False for a dialog that must be answered with its own buttons: Escape
   * does nothing, and Android Back is swallowed (the page behind stays) unless
   * `onBack` says otherwise. On by default.
   */
  dismissable?: boolean;
  /**
   * Android Back, when it must differ from Escape (utils/modal-stack.ts). The
   * circle agreement keeps Escape off but Back declines it (P5-1).
   */
  onBack?: () => void;
  /** Where focus lands on open instead of the dialog itself (useDialogFocus). */
  initialFocus?: RefObject<HTMLElement | null>;
}

export interface PromptDialog<T extends HTMLElement> {
  /** For the dialog's `aria-labelledby` and its title's `id`. */
  titleId: string;
  /** The `role="dialog"` element; give it `tabIndex={-1}`. */
  dialogRef: RefObject<T | null>;
  /** Whatever had focus as the dialog opened. */
  openerRef: RefObject<HTMLElement | null>;
}

const KEEP = (): void => undefined;

/**
 * THE APP'S DIALOG CONTRACT in one place (review round 1 of #1045, finding
 * 5; every dialog since the #1159 backlog): a title id, the dialog ref, the
 * opener captured before focus moves in, and useDialogFocus (focus in on
 * open, Tab kept inside, Escape is `onClose`, focus back to the opener, one
 * layer on the modal stack so Android Back answers it), stepping aside while
 * a gate is up. Every handler stays the caller's: pass a guarded `onClose`
 * when Escape must wait (Delete Account while its request is in flight), or
 * `dismissable: false` when only the dialog's buttons may answer it.
 *
 * The element: `role="dialog"` (or `alertdialog`), `aria-modal="true"`,
 * `aria-labelledby={titleId}` on its heading, `tabIndex={-1}`, `ref={dialogRef}`.
 */
export function usePromptDialog<T extends HTMLElement = HTMLDivElement>(
  isOpen: boolean,
  onClose: () => void,
  {
    yieldToGate = true,
    returnFocus = true,
    dismissable = true,
    onBack,
    initialFocus,
  }: PromptDialogOptions = {},
): PromptDialog<T> {
  const titleId = useId();
  const dialogRef = useRef<T | null>(null);
  const openerRef = useOpenerRef(isOpen);
  const gateUp = useGateOpen();
  const escape = dismissable ? onClose : KEEP;
  useDialogFocus(isOpen && !(yieldToGate && gateUp), dialogRef, {
    onEscape: escape,
    onBack: onBack ?? escape,
    returnFocusTo: returnFocus ? openerRef : undefined,
    initialFocus,
  });
  return { titleId, dialogRef, openerRef };
}
